// ── 예외 스케줄 등록/수정 모달 ───────────────────────────────────────────────
import React, { useState } from 'react';
import { useToast } from '../../../components/Toast';
import MultiDatePicker from '../../../components/MultiDatePicker';
import { useBodyScrollLock } from '../../../hooks/useBodyScrollLock';
import { styles, getTodayYmd } from './utils';
import { formatTimeTyping, finalizeTime } from '../../../utils/timeInput';
import {
  stdModalHeaderBar, stdModalHeaderIconBox, stdModalHeaderTitle, stdModalCloseBtn,
} from '../adminlayout/designStandard';

const { baseInputStyle, primaryBtn, dangerBtn, grayBtn } = styles;

/**
 * 예외 스케줄 등록/수정 모달
 * Props:
 *   modal       — null이면 신규, { id, user_id, date_from, ... }이면 수정
 *   employees   — 직원 목록 (user_id 드롭다운용)
 *   ipAliases   — 출근지 드롭다운 목록
 *   onClose     — 닫기 콜백
 *   onSave(payloads, editId) — 저장 콜백
 *   onDelete    — 삭제 콜백
 */
export function OverrideModal({ modal, employees, ipAliases, onClose, onSave, onDelete }) {
  useBodyScrollLock(true);
  const isEdit = Boolean(modal?.id);
  const { showToast } = useToast();

  const [form, setFormState] = useState({
    user_id:       modal?.user_id       ?? '',
    is_workday:    modal?.is_workday    ?? true,
    start_time:    modal?.start_time    || '09:30',
    end_time:      modal?.end_time      || '18:00',
    break_minutes: modal?.break_minutes ?? '',
    work_location: modal?.work_location || '',
    reason:        modal?.reason        || '',
  });

  // 등록 모드: 달력에서 선택한 날짜 배열 / 수정 모드: date_from ~ date_to 범위
  const [selectedDates, setSelectedDates] = useState(modal?.date_from ? [modal.date_from] : []);
  const [editDateFrom, setEditDateFrom] = useState(modal?.date_from || getTodayYmd());
  const [editDateTo,   setEditDateTo]   = useState(modal?.date_to   || getTodayYmd());
  const [saving, setSaving] = useState(false);

  function setField(field, value) {
    setFormState((prev) => ({ ...prev, [field]: value }));
  }

  async function handleSave() {
    setSaving(true);
    try {
      const basePayload = {
        ...form,
        user_id:       form.user_id       === '' ? null : Number(form.user_id),
        break_minutes: form.break_minutes === '' ? null : Number(form.break_minutes),
        work_location: form.work_location || null,
      };

      if (isEdit) {
        // 수정: 단일 날짜 범위 페이로드
        await onSave({ ...basePayload, date_from: editDateFrom, date_to: editDateTo }, modal.id);
      } else {
        // 등록: 선택된 날짜마다 개별 예외 스케줄 생성
        if (selectedDates.length === 0) {
          showToast('날짜를 하나 이상 선택해주세요.', 'error');
          setSaving(false);
          return;
        }
        const payloads = selectedDates.map((date) => ({ ...basePayload, date_from: date, date_to: date }));
        await onSave(payloads, null);
      }
      onClose();
    } catch (err) {
      showToast(err.message || '저장 실패', 'error');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ background: 'var(--bg-card)', borderRadius: 12, width: 460, maxWidth: '95vw', maxHeight: '90vh', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* 제목 */}
        <div style={stdModalHeaderBar(12)}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, minWidth: 0 }}>
            <div style={stdModalHeaderIconBox}>⏰</div>
            <div style={stdModalHeaderTitle}>
              {isEdit ? '예외 스케줄 수정' : '예외 스케줄 등록'}
            </div>
          </div>
          <button onClick={onClose} style={stdModalCloseBtn}>×</button>
        </div>

        <div style={{ padding: '20px 28px 28px', overflowY: 'auto' }}>
        {/* 적용 직원 — 이름 버튼 칩 */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>적용 직원</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
            <button
              type="button"
              onClick={() => setField('user_id', '')}
              style={{
                padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                cursor: 'pointer', border: '1.5px solid',
                background: form.user_id === '' ? '#6366f1' : 'var(--bg-subtle)',
                color:      form.user_id === '' ? '#fff'    : 'var(--text-muted)',
                borderColor: form.user_id === '' ? '#6366f1' : 'var(--border)',
              }}
            >전체</button>
            {employees.map((emp) => {
              const selected = String(form.user_id) === String(emp.id);
              return (
                <button
                  key={emp.id}
                  type="button"
                  onClick={() => setField('user_id', emp.id)}
                  style={{
                    padding: '5px 12px', borderRadius: 999, fontSize: 12, fontWeight: 600,
                    cursor: 'pointer', border: '1.5px solid',
                    background: selected ? '#6366f1' : 'var(--bg-subtle)',
                    color:      selected ? '#fff'    : 'var(--text-sub)',
                    borderColor: selected ? '#6366f1' : 'var(--border)',
                  }}
                >{emp.name}{employees.filter((e) => e.name === emp.name).length > 1 ? ` (${emp.company_code || 'PINE'})` : ''}</button>
              );
            })}
          </div>
        </div>

        {/* 적용 날짜 */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>적용 날짜</label>
          {isEdit ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>시작일</label>
                <input type="date" value={editDateFrom} onChange={(e) => setEditDateFrom(e.target.value)} style={{ ...baseInputStyle, width: '100%', fontSize: 12 }} />
              </div>
              <div>
                <label style={{ fontSize: 11, color: 'var(--text-muted)', display: 'block', marginBottom: 2 }}>종료일</label>
                <input type="date" value={editDateTo}   onChange={(e) => setEditDateTo(e.target.value)}   style={{ ...baseInputStyle, width: '100%', fontSize: 12 }} />
              </div>
            </div>
          ) : (
            <MultiDatePicker selectedDates={selectedDates} onChange={setSelectedDates} />
          )}
        </div>

        {/* 근무 여부 */}
        <div style={{ marginBottom: 14 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 6 }}>근무 여부</label>
          <div style={{ display: 'flex', gap: 12 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="radio" checked={form.is_workday} onChange={() => setField('is_workday', true)} />
              <span style={{ fontSize: 13 }}>근무일</span>
            </label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
              <input type="radio" checked={!form.is_workday} onChange={() => setField('is_workday', false)} />
              <span style={{ fontSize: 13 }}>휴무일</span>
            </label>
          </div>
        </div>

        {/* 근무 시간 + 출근지 (근무일인 경우만) */}
        {form.is_workday && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginBottom: 14 }}>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>출근시간</label>
                <input type="text" inputMode="numeric" placeholder="0930 또는 09:30" value={form.start_time} onChange={(e) => setField('start_time', formatTimeTyping(e.target.value))} onBlur={(e) => setField('start_time', finalizeTime(e.target.value))} style={{ ...baseInputStyle, width: '100%' }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>퇴근시간</label>
                <input type="text" inputMode="numeric" placeholder="1800 또는 18:00" value={form.end_time} onChange={(e) => setField('end_time', formatTimeTyping(e.target.value))} onBlur={(e) => setField('end_time', finalizeTime(e.target.value))} style={{ ...baseInputStyle, width: '100%' }} />
              </div>
              <div>
                <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>휴게(분)</label>
                <input type="text" inputMode="numeric" value={form.break_minutes} onChange={(e) => setField('break_minutes', e.target.value)} placeholder="미설정" style={{ ...baseInputStyle, width: '100%' }} />
              </div>
            </div>
            <div style={{ marginBottom: 14 }}>
              <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>출근지</label>
              <select value={form.work_location} onChange={(e) => setField('work_location', e.target.value)} style={{ ...baseInputStyle, width: '100%' }}>
                <option value="">— 기본값 사용 —</option>
                {/* ip-aliases 는 객체 배열 { user_id, label, is_active } (2026-04-29~). 객체를 그대로 렌더하면
                    "Objects are not valid as a React child" 로 모달이 크래시 → label 로 렌더. 형제 모달과 동일 패턴.
                    선택 직원(form.user_id) 개인 별칭 + 통합(user_id NULL) 별칭만 노출. */}
                {ipAliases
                  .filter((a) => {
                    if (typeof a === 'string') return true; // 옛 응답 호환
                    if (!a) return false;
                    if (a.is_active === false) return false;
                    if (a.user_id === null || a.user_id === undefined) return true; // 통합
                    return Number(a.user_id) === Number(form.user_id); // 개인
                  })
                  .map((a, idx) => {
                    const label = typeof a === 'string' ? a : a.label;
                    if (!label) return null;
                    return <option key={`${label}-${idx}`} value={label}>{label}</option>;
                  })}
              </select>
            </div>
          </>
        )}

        {/* 사유 */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 13, fontWeight: 600, display: 'block', marginBottom: 4 }}>변경 사유</label>
          <input type="text" value={form.reason} onChange={(e) => setField('reason', e.target.value)} placeholder="예: 이번 주 월요일만 파트타임 근무" style={{ ...baseInputStyle, width: '100%' }} />
        </div>

        {/* 버튼 */}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'space-between', alignItems: 'center' }}>
          {isEdit && onDelete ? (
            <button onClick={() => { if (!window.confirm('이 예외 스케줄을 삭제하시겠습니까?')) return; onDelete(modal.id); }} style={dangerBtn}>삭제</button>
          ) : (
            <div />
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={grayBtn}>취소</button>
            <button onClick={handleSave} disabled={saving} style={primaryBtn}>
              {saving ? '저장 중...' : (isEdit ? '수정' : `등록${selectedDates.length > 1 ? ` (${selectedDates.length}건)` : ''}`)}
            </button>
          </div>
        </div>
        </div>
      </div>
    </div>
  );
}
